"use client";

import {
  Artwork as ArtworkType,
  Asset,
  ContactAssistant,
  SearchField as SearchFieldType,
  SupportModule as SupportModuleType,
} from "../../../../../__generated__/contentful-types";
import { useImageLoading } from "../../../../../helpers/ImageLoadingContext";
import { useLocaleStringContext } from "../../../../../helpers/LocaleStringContext";
import { getContentfulImageUrl } from "../../../../../helpers/contentful/images/getContentfulImageUrl";
import {
  isArtwork,
  isButton,
  isContactAssistant,
  isSearchField,
  isSupportModule,
} from "../../../../../helpers/contentful/typeguards";
import { useViewportDimensions } from "../../../../../helpers/useViewportDimensions";
import { Kicker } from "../../../../kicker/kicker";
import { ContentfulResponsiveImage } from "../../../contentful-responsive-image/contentful-responsive-image";
import {
  CosmosButton,
  CosmosIconFeedback,
  CosmosIconSearch,
} from "@cosmos/web-react";
import classNames from "classnames/bind";
import { usePathname } from "next/navigation";
import { Fragment } from "react";
import styles from "./support-module.module.css";

const DESKTOP_BREAKPOINT = 1000;
const cx = classNames.bind(styles);

export const SupportModule = ({
  module,
}: {
  module: SupportModuleType | ArtworkType;
}) => {
  const { viewportWidth } = useViewportDimensions();

  return (
    <div
      className={cx("container", {
        "container--white":
          viewportWidth < DESKTOP_BREAKPOINT &&
          isSupportModule(module) &&
          (isSearchField(module.interaction) ||
            isButton(module.interaction) ||
            isContactAssistant(module.interaction)),
        "container--single": isArtwork(module),
      })}
    >
      {isArtwork(module) ? (
        viewportWidth >= DESKTOP_BREAKPOINT &&
        module.artwork && <Artwork asset={module.artwork} />
      ) : (
        <Module module={module} viewportWidth={viewportWidth} />
      )}
    </div>
  );
};

function Artwork({ asset }: { asset: Asset }) {
  return (
    <div className={cx("artwork")}>
      <ContentfulResponsiveImage
        asset={asset}
        className={cx("artwork-image")}
      />
    </div>
  );
}

const Module = ({
  module,
  viewportWidth,
}: {
  module: SupportModuleType;
  viewportWidth: number;
}) => {
  if (isSearchField(module.interaction)) {
    return viewportWidth < DESKTOP_BREAKPOINT ? (
      <Fragment>
        <div className={cx("inner")}>
          <Kicker
            kind="underlined"
            text={module.name}
            className={cx("kicker")}
          />
          <h2 className={cx("title", "title--small")}>{module.title}</h2>
        </div>
        <SearchField props={module.interaction} />
      </Fragment>
    ) : (
      <Fragment>
        <div className={cx("inner")}>
          <Kicker
            kind="underlined"
            text={module.name}
            className={cx("kicker")}
          />
          <h2 className={cx("title")}>{module.title}</h2>
          <p className={cx("description")}>{module.description}</p>
        </div>
        <SearchField props={module.interaction} />
      </Fragment>
    );
  }
  if (isButton(module.interaction)) {
    return viewportWidth < DESKTOP_BREAKPOINT ? (
      <Fragment>
        <Kicker kind="underlined" text={module.name} className={cx("kicker")} />
        <h2 className={cx("title")}>{module.title}</h2>
        <p className={cx("description", "description--small")}>
          {module.description}
        </p>
        <div className={cx("button-container")}>
          <CosmosButton
            href={module.interaction.url ?? undefined}
            size="large"
            kind="primary"
            className={cx("button-button")}
          >
            {module.interaction.text}
          </CosmosButton>
          {module.linkText && module.linkUrl && (
            <CosmosButton
              href={module.linkUrl}
              size="large"
              kind="tertiary"
              appearance="dark"
              className={cx("button-button")}
            >
              {module.linkText}
            </CosmosButton>
          )}
        </div>
      </Fragment>
    ) : (
      <Fragment>
        <div className={cx("inner")}>
          <Kicker
            kind="underlined"
            text={module.name}
            className={cx("kicker")}
          />
          <h2 className={cx("title")}>{module.title}</h2>
          <p className={cx("description")}>{module.description}</p>
        </div>
        <div className={cx("button-container")}>
          <CosmosButton
            href={module.interaction.url ?? undefined}
            size="large"
            kind="primary"
            className={cx("button-button")}
          >
            {module.interaction.text}
          </CosmosButton>
          {module.linkText && module.linkUrl && (
            <CosmosButton
              href={module.linkUrl}
              size="large"
              kind="tertiary"
              appearance="dark"
              className={cx("button-button")}
            >
              {module.linkText}
            </CosmosButton>
          )}
        </div>
      </Fragment>
    );
  }
  if (isContactAssistant(module.interaction)) {
    return viewportWidth < DESKTOP_BREAKPOINT ? (
      <Fragment>
        <div className={cx("inner")}>
          <Kicker
            kind="underlined"
            text={module.name}
            className={cx("kicker")}
          />
          <h2 className={cx("title", "title--small")}>{module.title}</h2>
          <BotSection interaction={module.interaction} />
        </div>
      </Fragment>
    ) : (
      <Fragment>
        <div className={cx("inner")}>
          <Kicker
            kind="underlined"
            text={module.name}
            className={cx("kicker")}
          />
          <h2 className={cx("title")}>{module.title}</h2>
          <p className={cx("description")}>{module.description}</p>
          <BotSection interaction={module.interaction} />
        </div>
      </Fragment>
    );
  }
  return null;
};

function BotSection({ interaction }: { interaction: ContactAssistant }) {
  const imageLoading = useImageLoading();
  return (
    <div className={cx("bot-animation-container")}>
      {interaction.botImage?.url && (
        <picture>
          {/*
           * HACK BUG: animated AVIF has black background in Safari (2024 August)
           * Maybe in the future we can updated the array back to: ["avif", "webp"]
           */}
          {(["webp"] as const).map((format, index) => (
            <source
              key={index}
              type={`image/${format}`}
              srcSet={[120, 240]
                .map((width) => {
                  const src = getContentfulImageUrl(
                    interaction.botImage?.url ?? "",
                    {
                      fm: format,
                      width,
                    },
                  );

                  return `${src} ${width}w`;
                })
                .join(", ")}
            />
          ))}

          <img
            loading={imageLoading}
            src={interaction.botImage.url}
            sizes="120px"
            className={cx("bot-animation")}
            alt={interaction.botImage.description ?? ""}
          />
        </picture>
      )}
      <CosmosButton
        href={interaction.buttonUrl ?? undefined}
        size="large"
        kind="primary"
        iconPlacement="before"
        className={cx("assistant-button")}
      >
        <CosmosIconFeedback
          slot="icon"
          className={cx("assistant-button-icon")}
        />
        {interaction.buttonText}
      </CosmosButton>
    </div>
  );
}

const SearchField = ({ props }: { props: SearchFieldType }) => {
  const localeString = useLocaleStringContext();
  const route = usePathname();

  const isSugarFreePage =
    (route?.includes("red-bull-sugarfree") ||
      route?.includes("red-bull-zero")) ??
    false;

  const isZeroCaloriesPage = route?.includes("red-bull-zero-calories") ?? false;
  const isEditionPage =
    (route?.includes("edition") || route?.includes("editions")) ?? false;

  const hasCategory = isSugarFreePage || isZeroCaloriesPage || isEditionPage;

  const categoryRoute = getCategoryRoute(
    route ?? "",
    isSugarFreePage,
    isZeroCaloriesPage,
    isEditionPage,
    localeString,
  );

  return (
    <form className={cx("search-field")} action={props.url ?? undefined}>
      <a
        className={cx("search-field-button")}
        href={props.url ? `${props.url}?category=${categoryRoute}` : undefined}
      >
        <CosmosIconSearch
          className={cx("search-field-icon")}
          aria-hidden="true"
        />
      </a>
      {hasCategory && (
        <input
          type="hidden"
          name="category"
          value={categoryRoute ?? undefined}
        />
      )}
      <input
        className={cx("search-field-input")}
        name="keywords"
        placeholder={props.text ?? undefined}
      />
    </form>
  );
};

function getCategoryRoute(
  route: string,
  isSugarFreePage: boolean,
  isZeroCaloriesPage: boolean,
  isEditionPage: boolean,
  localeString: string,
): string | null {
  if (isSugarFreePage || isZeroCaloriesPage) {
    return route;
  }

  if (isEditionPage) {
    return `/${localeString}/energydrink/red-bull-editions`;
  }

  return null;
}
